import { useCallback, useEffect, useState } from "react";
import api from "../../../services/api";

/**
 * Busca dados fiscais reais extraídos do SPED (ICMS/PIS-COFINS já processados
 * pelo WORKER) para alimentar o simulador de Reforma Tributária.
 *
 * Se o projeto ainda não tiver SPEDs processados, `mode` retorna "mock" e os
 * componentes devem usar os dados de exemplo (`mockData*.js`) normalmente.
 * Falha na consulta é "error": não pode ser confundida com projeto sem dados.
 * Ao refazer a consulta, os dados atuais continuam na tela até a resposta chegar.
 */
export default function useReformaFiscalData(anoOrFiltros) {
  // Aceita um ano (legado) ou o objeto de filtros do FiltroCalculoModal:
  // { ano, dataInicio: "YYYY-MM-DD", dataFim: "YYYY-MM-DD", empresas: [cnpj,...] }
  const filtros =
    typeof anoOrFiltros === "object" && anoOrFiltros !== null
      ? anoOrFiltros
      : { ano: anoOrFiltros };
  const filtrosKey = JSON.stringify(filtros);
  const [reloadKey, setReloadKey] = useState(0);
  const reload = useCallback(() => setReloadKey((k) => k + 1), []);

  const [state, setState] = useState({
    mode: "loading", // "loading" | "real" | "mock"
    loading: true,
    error: null,
    dadosMensais: null,
    totalMensal: null,
    creditos: null,
  });

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setState((prev) => ({ ...prev, mode: prev.mode === "real" ? "real" : "loading", loading: true, error: null }));

      try {
        const statusResp = await api.get("/reforma/fiscal-data/status");
        const hasSpedData = statusResp?.data?.data?.hasSpedData;

        if (!hasSpedData) {
          if (!cancelled) {
            setState({
              mode: "mock",
              loading: false,
              error: null,
              dadosMensais: null,
              totalMensal: null,
              creditos: null,
            });
          }
          return;
        }

        const params = {};
        if (filtros.dataInicio || filtros.dataFim) {
          if (filtros.dataInicio) params.dataInicio = filtros.dataInicio;
          if (filtros.dataFim) params.dataFim = filtros.dataFim;
        } else if (filtros.ano) {
          params.ano = filtros.ano;
        }
        if (filtros.empresas && filtros.empresas.length > 0) {
          params.empresas = filtros.empresas.join(",");
        }

        const [mensalResp, creditosResp] = await Promise.all([
          api.get("/reforma/fiscal-data/mensal", { params }),
          api.get("/reforma/fiscal-data/creditos", { params }),
        ]);

        const mensalData = mensalResp?.data?.data || {};
        const creditos = creditosResp?.data?.data || null;

        // Se nenhum endpoint trouxe dado aproveitável, cai para mock
        const temAlgumDado = (mensalData.dadosMensais && mensalData.dadosMensais.length > 0) || creditos;

        if (!cancelled) {
          setState({
            mode: temAlgumDado ? "real" : "mock",
            loading: false,
            error: null,
            updatedAt: new Date().toISOString(),
            dadosMensais: mensalData.dadosMensais || [],
            totalMensal: mensalData.totalMensal || null,
            fontes: mensalData.fontes || null,
            creditos,
          });
        }
      } catch (error) {
        console.error("[useReformaFiscalData] Erro ao buscar dados fiscais:", error);
        if (!cancelled) {
          setState({
            mode: "error",
            loading: false,
            error: error.message,
            dadosMensais: null,
            totalMensal: null,
            creditos: null,
          });
        }
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [filtrosKey, reloadKey]); // eslint-disable-line react-hooks/exhaustive-deps

  return { ...state, reload };
}
